/**
 * SeasonalEffect – leichte Canvas-Ebene für saisonale Optik-Events (Schnee, Laub, Funken, Feuerwerk, Blüten).
 * Wird über ein Admin-Event (event_type = seasonal_effect) gesteuert. Fängt keine Klicks ab, pausiert bei
 * versteckter Seite, respektiert „Bewegung reduzieren“ und den Spieler-Schalter (localStorage `seasonal-effects`).
 */
import { Component, createEffect, onCleanup, Show } from 'solid-js';
import { useLocation } from '@solidjs/router';
import { useSeason } from '../utils/seasonal';

export { seasonalEffectsEnabled, setSeasonalEffectsEnabled } from '../utils/seasonal';

/** Nur auf ruhigen Seiten – nicht über Tabellen/Formularen/Admin */
const ALLOWED_PATHS = ['/', '/dashboard', '/login', '/register', '/news'];

interface Particle { x: number; y: number; vx: number; vy: number; r: number; a: number; rot: number; vr: number; life: number; hue: number; phase: number }

const LEAF_COLORS = ['#c2410c', '#d97706', '#b45309', '#9a3412', '#ca8a04'];
const BLOSSOM_COLORS = ['#fbcfe8', '#f9a8d4', '#fce7f3', '#ffffff'];

const SeasonalEffect: Component = () => {
  const location = useLocation();
  const { season, isPreview } = useSeason();
  let canvas: HTMLCanvasElement | undefined;

  const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const pathOk = () => ALLOWED_PATHS.includes(location.pathname.replace(/\/$/, '') || '/');
  const current = season;
  const active = () => !!season() && !reduceMotion() && (isPreview() || pathOk());

  createEffect(() => {
    const eff = current();
    if (!active() || !eff || !canvas) return;
    const cv = canvas;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    let w = 0, h = 0, raf = 0, last = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      w = window.innerWidth; h = window.innerHeight;
      cv.width = Math.floor(w * dpr); cv.height = Math.floor(h * dpr);
      cv.style.width = w + 'px'; cv.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const small = window.innerWidth < 640 || (navigator.hardwareConcurrency || 4) <= 2;
    const count = Math.round((small ? 35 : 80) * eff.intensity);
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    let parts: Particle[] = [];

    const make = (fromTop: boolean): Particle => {
      const base: Particle = { x: rnd(0, w), y: fromTop ? rnd(-40, -5) : rnd(0, h), vx: 0, vy: 0, r: 2, a: 1, rot: rnd(0, 6.28), vr: rnd(-1, 1), life: 1, hue: 0, phase: rnd(0, 6.28) };
      switch (eff.name) {
        case 'snow': base.r = rnd(1.2, 3.6); base.vy = rnd(25, 60) * (base.r / 3); base.vx = rnd(-8, 8); base.a = rnd(0.5, 0.95); break;
        case 'leaves': base.r = rnd(5, 9); base.vy = rnd(30, 60); base.vx = rnd(-20, 20); base.a = 0.9; base.hue = Math.floor(rnd(0, LEAF_COLORS.length)); base.vr = rnd(-2, 2); break;
        case 'blossom': base.r = rnd(3, 6); base.vy = rnd(20, 40); base.vx = rnd(-15, 15); base.a = 0.85; base.hue = Math.floor(rnd(0, BLOSSOM_COLORS.length)); break;
        case 'sparks': base.x = rnd(0, w); base.y = fromTop ? h + 10 : rnd(0, h); base.r = rnd(1, 2.6); base.vy = -rnd(25, 70); base.vx = rnd(-12, 12); base.a = rnd(0.4, 1); base.hue = rnd(18, 42); break;
      }
      return base;
    };

    const burst = () => {
      const cx = rnd(w * 0.15, w * 0.85), cy = rnd(h * 0.1, h * 0.5), hue = rnd(0, 360);
      const n = small ? 28 : 50;
      for (let i = 0; i < n; i++) {
        const ang = (i / n) * 6.28 + rnd(-0.1, 0.1), sp = rnd(70, 190);
        parts.push({ x: cx, y: cy, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, r: rnd(1.2, 2.2), a: 1, rot: 0, vr: 0, life: 1, hue: hue + rnd(-15, 15), phase: 0 });
      }
    };

    if (eff.name === 'fireworks') { parts = []; burst(); } else { parts = Array.from({ length: count }, () => make(false)); }
    let nextBurst = 0;

    // Halloween: Fledermäuse, die quer über den Bildschirm flattern
    const bats = eff.name === 'sparks'
      ? Array.from({ length: Math.max(2, Math.round((small ? 2 : 4) * eff.intensity)) }, () => ({
          x: rnd(0, w), y: rnd(h * 0.08, h * 0.55), vx: (Math.random() < 0.5 ? -1 : 1) * rnd(50, 110), size: rnd(16, 26), phase: rnd(0, 6.28), amp: rnd(14, 34),
        }))
      : [];

    const draw = (t: number) => {
      raf = requestAnimationFrame(draw);
      if (document.hidden) { last = t; return; }
      const dt = Math.min(0.05, (t - (last || t)) / 1000);
      last = t;
      ctx.clearRect(0, 0, w, h);

      if (eff.name === 'fireworks') {
        nextBurst -= dt;
        if (nextBurst <= 0 && parts.length < 400 * eff.intensity) { burst(); nextBurst = rnd(0.8, 2.2) / eff.intensity; }
        for (let i = parts.length - 1; i >= 0; i--) {
          const p = parts[i];
          p.vy += 90 * dt; p.vx *= 0.985; p.x += p.vx * dt; p.y += p.vy * dt; p.life -= dt * 0.7;
          if (p.life <= 0) { parts.splice(i, 1); continue; }
          ctx.globalAlpha = Math.max(0, p.life);
          ctx.fillStyle = `hsl(${p.hue},95%,62%)`;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.28); ctx.fill();
        }
        ctx.globalAlpha = 1;
        return;
      }

      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        p.phase += dt;
        p.x += (p.vx + Math.sin(p.phase * 1.4) * 12) * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        const off = eff.name === 'sparks' ? p.y < -10 : p.y > h + 12;
        if (off || p.x < -20 || p.x > w + 20) { parts[i] = make(true); continue; }
        ctx.globalAlpha = p.a;
        if (eff.name === 'snow') {
          ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.28); ctx.fill();
        } else if (eff.name === 'sparks') {
          const flick = 0.5 + 0.5 * Math.sin(p.phase * 6);
          ctx.globalAlpha = p.a * flick;
          ctx.fillStyle = `hsl(${p.hue},100%,58%)`; ctx.shadowColor = `hsl(${p.hue},100%,55%)`; ctx.shadowBlur = 8;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.28); ctx.fill(); ctx.shadowBlur = 0;
        } else {
          ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
          ctx.fillStyle = eff.name === 'leaves' ? LEAF_COLORS[p.hue] : BLOSSOM_COLORS[p.hue];
          ctx.beginPath(); ctx.ellipse(0, 0, p.r, p.r * (eff.name === 'leaves' ? 0.55 : 0.7), 0, 0, 6.28); ctx.fill();
          ctx.restore();
        }
      }
      ctx.globalAlpha = 1;

      if (bats.length) {
        ctx.font = '22px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
        for (const b of bats) {
          b.x += b.vx * dt; b.phase += dt * 5;
          if (b.vx > 0 && b.x > w + 60) b.x = -60; else if (b.vx < 0 && b.x < -60) b.x = w + 60;
          const y = b.y + Math.sin(b.phase * 0.6) * b.amp;
          ctx.save(); ctx.translate(b.x, y); ctx.scale((b.vx > 0 ? 1 : -1) * (0.85 + 0.15 * Math.sin(b.phase * 2)), b.size / 22);
          ctx.globalAlpha = 0.9 * Math.max(0, Math.min(1, Math.min(b.x + 60, w + 60 - b.x) / 120)); ctx.fillText('🦇', -11, 0); ctx.restore();
        }
      }
    };
    raf = requestAnimationFrame(draw);

    onCleanup(() => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); ctx.clearRect(0, 0, w, h); });
  });

  return (
    <Show when={active()}>
      <canvas ref={canvas} aria-hidden="true" style="position:fixed;inset:0;pointer-events:none;z-index:40;" />
    </Show>
  );
};

export default SeasonalEffect;
